import type { StateCreator } from 'zustand';

import { getWardrobeCategoriesByGender } from '@/constants/generated';
import { saveWardrobeItemsToDrive } from '@/services/wardrobeItemsStorageService';

import { getUserUploadedItems } from './helpers';
import type { IWardrobeStore } from './types';

export const initialWardrobe: IWardrobeStore['wardrobe'] = {
  categories: getWardrobeCategoriesByGender('female'),
  selectedItems: [],
  isLoading: false,
};

/**
 * Wardrobe slice — selectable wardrobe categories + the currently-selected
 * items. Mutating actions that change user-uploaded items also flush to Drive
 * via `saveWardrobeItemsToDrive` (fire-and-forget; backend dedupes by id).
 */
export type WardrobeSliceContribution = Pick<
  IWardrobeStore,
  | 'wardrobe'
  | 'selectWardrobeItem'
  | 'deselectWardrobeItem'
  | 'clearSelectedItems'
  | 'addUserWardrobeItem'
  | 'removeUserWardrobeItem'
  | 'updateWardrobeItemUrl'
  | 'setWardrobeItemThumbnail'
  | 'setWardrobeItemProcessing'
>;

export const createWardrobeSlice: StateCreator<IWardrobeStore, [], [], WardrobeSliceContribution> = (set, get) => ({
  wardrobe: initialWardrobe,

  selectWardrobeItem: newItem =>
    set(s => {
      if (s.wardrobe.selectedItems.some(item => item.id === newItem.id)) return s;
      // Complete Outfit replaces all clothing — other items can't coexist with an outfit reference
      if (newItem.category === 'complete-outfits') {
        return { wardrobe: { ...s.wardrobe, selectedItems: [newItem] } };
      }
      const withoutSameCategory = s.wardrobe.selectedItems.filter(item => item.category !== newItem.category);
      return { wardrobe: { ...s.wardrobe, selectedItems: [...withoutSameCategory, newItem] } };
    }),

  deselectWardrobeItem: id =>
    set(s => ({
      wardrobe: {
        ...s.wardrobe,
        selectedItems: s.wardrobe.selectedItems.filter(item => item.id !== id),
      },
    })),

  clearSelectedItems: () => set(s => ({ wardrobe: { ...s.wardrobe, selectedItems: [] } })),

  addUserWardrobeItem: (item, category) => {
    set(s => {
      const idx = s.wardrobe.categories.findIndex(cat => cat.id === category);
      if (idx === -1) return s;
      const newCategories = [...s.wardrobe.categories];
      newCategories[idx] = { ...newCategories[idx], items: [item, ...newCategories[idx].items] };
      return { wardrobe: { ...s.wardrobe, categories: newCategories } };
    });
    void saveWardrobeItemsToDrive(getUserUploadedItems(get().wardrobe.categories), 'addUserWardrobeItem');
  },

  removeUserWardrobeItem: itemId => {
    set(s => ({
      wardrobe: {
        ...s.wardrobe,
        categories: s.wardrobe.categories.map(cat => ({
          ...cat,
          items: cat.items.filter(item => item.id !== itemId),
        })),
        selectedItems: s.wardrobe.selectedItems.filter(item => item.id !== itemId),
      },
    }));
    void saveWardrobeItemsToDrive(getUserUploadedItems(get().wardrobe.categories), 'removeUserWardrobeItem');
  },

  updateWardrobeItemUrl: (itemId, cdnUrl) => {
    set(s => ({
      wardrobe: {
        ...s.wardrobe,
        categories: s.wardrobe.categories.map(cat => ({
          ...cat,
          items: cat.items.map(item => (item.id === itemId ? { ...item, thumbnailUrl: cdnUrl, imageUrl: cdnUrl } : item)),
        })),
        selectedItems: s.wardrobe.selectedItems.map(item => (item.id === itemId ? { ...item, thumbnailUrl: cdnUrl, imageUrl: cdnUrl } : item)),
      },
    }));
    void saveWardrobeItemsToDrive(getUserUploadedItems(get().wardrobe.categories), 'updateWardrobeItemUrl');
  },

  setWardrobeItemThumbnail: (itemId, thumbnailUrl) => {
    set(s => ({
      wardrobe: {
        ...s.wardrobe,
        categories: s.wardrobe.categories.map(cat => ({
          ...cat,
          items: cat.items.map(item => (item.id === itemId ? { ...item, thumbnailUrl } : item)),
        })),
        selectedItems: s.wardrobe.selectedItems.map(item => (item.id === itemId ? { ...item, thumbnailUrl } : item)),
      },
    }));
    void saveWardrobeItemsToDrive(getUserUploadedItems(get().wardrobe.categories), 'setWardrobeItemThumbnail');
  },

  setWardrobeItemProcessing: (itemId, processing) =>
    set(s => ({
      wardrobe: {
        ...s.wardrobe,
        categories: s.wardrobe.categories.map(cat => ({
          ...cat,
          items: cat.items.map(item => (item.id === itemId ? { ...item, isProcessing: processing } : item)),
        })),
      },
    })),
});
